import { useEffect } from 'react'
import { detectPlatform, trackStat } from '@/controller/statsTracking.ts'
import { isRunningStandalone } from '@/controller/pwaInstall.ts'
import { StatPlatform } from '@/models/stats.ts'

/**
 * Per drive, not per device: every gallery installs as its own app (own name,
 * own icon, own start URL), so installing a second gallery is a second install.
 * Mirrors the `app-icon-link-shown-<driveId>` key next door.
 */
const INSTALL_FLAG_PREFIX = 'heyalbum:app-install-reported:'

function hasReportedInstall(driveId: string): boolean {
  try {
    return localStorage.getItem(INSTALL_FLAG_PREFIX + driveId) === '1'
  } catch {
    return false
  }
}

function markInstallReported(driveId: string) {
  try {
    localStorage.setItem(INSTALL_FLAG_PREFIX + driveId, '1')
  } catch {
    // private mode: then the 24h backend dedup window is the only net
  }
}

/** Reports the install once per drive and device. Further calls are a no-op. */
export function reportAppInstall(driveId: string, platform: StatPlatform = detectPlatform()) {
  if (hasReportedInstall(driveId)) return
  markInstallReported(driveId)
  trackStat({ kind: 'app_install', drive: driveId, source: 'app', platform })
}

/**
 * `app_view` + `app_install` for an installed gallery. Hook it into the gallery
 * root once; the two platforms need different handling and both are covered here.
 *
 * The platforms are NOT comparable without splitting by `platform`:
 * - **Chromium (Android):** the browser fires a real `appinstalled` event, so the
 *   number is an actual installation at the moment it happens.
 * - **iOS:** there is no callback for "Add to Home Screen" at all. The first
 *   launch in standalone mode is the only signal we get, which makes iOS numbers
 *   a lower bound (an install that is never opened stays invisible) and shifts
 *   their timestamp to that first launch.
 *
 * Because that iOS signal happens on *every* launch, the localStorage flag is
 * what keeps it to one install per drive. The backend's 24h dedup window is
 * currently off, so that flag is the only safeguard.
 */
export function useAppTracking(driveId: string | undefined) {
  useEffect(() => {
    if (!driveId) return
    const platform = detectPlatform()

    // Chromium: the browser's real install event.
    const onInstalled = () => reportAppInstall(driveId)
    window.addEventListener('appinstalled', onInstalled)

    if (isRunningStandalone()) {
      // Every launch from the installed app.
      trackStat({ kind: 'app_view', drive: driveId, source: 'app', platform })

      // iOS has no install callback: the first standalone launch IS the install
      // signal. On Chromium this is the fallback for a missed `appinstalled`
      // (e.g. installed through the browser menu instead of our prompt).
      reportAppInstall(driveId, platform)
    }

    return () => window.removeEventListener('appinstalled', onInstalled)
  }, [driveId])
}
